import type { AxiosResponse } from "axios";
import { isAxiosError } from "axios";

import type { ApiResponse, ApiResponseWithPaginationMeta, ErrorResponse, ErrorResult } from "~/api/apiResponseTypes.ts";
import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import type { PrivateFileDownloadResponse } from "~/types/fileTypes.ts";

export const getDataFromResponse = <T>(res: AxiosResponse<ApiResponse<T>>): T => res.data.data;
export const getRootDataFromResponse = <T>(res: AxiosResponse<T>): T => res.data;

export const getPaginatedDataFromResponse = <T>(response: AxiosResponse<ApiResponseWithPaginationMeta<T>>) => {
    return {
        data: response.data.data,
        meta: response.data.meta,
    };
};

export function getErrorMessage(error: unknown): string {
    return buildErrorStringFromErrorResult(processApiError(error));
}

export function processApiError(error: unknown): ErrorResult {
    if (isAxiosError(error)) {
        const errorCode = error.response?.status || 0;

        if (errorCode === 422) {
            const validationErrors: ErrorResponse = error.response?.data ?? { errors: {} };

            return {
                type: ApiResponseErrorType.VALIDATION,
                code: errorCode,
                messages: validationErrors.errors,
            };
        } else {
            return {
                type: ApiResponseErrorType.AXIOS,
                code: errorCode,
                message: error.message,
                originalMessage: error.response?.data.message || error.response?.data || null,
            };
        }
    } else {
        return {
            type: ApiResponseErrorType.UNKNOWN,
            message: String(error),
        };
    }
}

export const buildErrorStringFromErrorResult = (errorResult: ErrorResult): string => {
    switch (errorResult.type) {
        case ApiResponseErrorType.VALIDATION: {
            const messages = Object.values(errorResult.messages).flat();
            return `Validation Error${messages.length > 1 ? "s" : ""}: ${messages.join("; ")}`;
        }
        case ApiResponseErrorType.AXIOS:
            return `Request failed with status code ${errorResult.code}: ${errorResult.originalMessage || errorResult.message}`;
        case ApiResponseErrorType.UNKNOWN:
            return `An unknown error occurred: ${errorResult.message}`;
        default:
            return "An unexpected error occurred.";
    }
};

export function getBlobFromResponse(response: AxiosResponse<ApiResponse<PrivateFileDownloadResponse>>): Blob {
    const base64File = getDataFromResponse(response).fileBase64;
    const binaryString = window.atob(base64File);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);

    for (let i = 0; i < binaryLen; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: "application/octet-stream" });
}
